import { DifficultyLevel } from '../interfaces/question.interface';
import { VaultCrackerRound } from '../interfaces/vault-cracker.interface';

/**
 * Curated "percent of a quantity" problems for Vault Cracker (Grade 6).
 *
 * The pools are hand-picked so every answer is a positive integer strictly
 * inside the vault (0 < answer < whole where that makes sense) and lands on a
 * snap tick. Distractor *misconceptions* live in the shell's trap classifier,
 * not here — the builder only guarantees a clean, ramping session.
 *
 * A default session is the ratified interleave
 *   [easy partOf, easy partOf, medium partOf, medium partOf, bridge,
 *    hard findWhole, hard findPercent]
 * which walks a student from benchmark percents up to the two inverse
 * sub-skills. Rounds are drawn at random from each pool for replayability.
 *
 * Guardrails enforced by `validPartLike`: never emit a partOf/bridge/findWhole
 * round whose dialled answer equals the percent number (that would silently
 * reward the "percent = part" misconception), and keep percents on the
 * benchmark set {5,10,15,20,25,30,40,50,60,75}.
 */

type Cat = 'easy' | 'medium' | 'bridge' | 'hardPart' | 'hardWhole' | 'hardPercent';

/** Raw pool entry before it is expanded into a full `VaultCrackerRound`. */
interface Spec {
  percent: number;    // percent number (for findPercent this is also the answer)
  whole: number;      // vault total / "of" quantity (for findWhole this equals answer)
  answer: number;     // the integer the student dials
  cat: Cat;
  bridgeChip?: { en: string; ar: string };
}

const ALLOWED_PERCENTS = new Set([5, 10, 15, 20, 25, 30, 40, 50, 60, 75]);

// ── Easy partOf — benchmark percents (50/25/10) of friendly wholes ──
const EASY_PART_OF: Spec[] = [
  { percent: 50, whole: 40, answer: 20, cat: 'easy' },
  { percent: 10, whole: 60, answer: 6,  cat: 'easy' },
  { percent: 25, whole: 80, answer: 20, cat: 'easy' },
  { percent: 50, whole: 60, answer: 30, cat: 'easy' },
  { percent: 10, whole: 90, answer: 9,  cat: 'easy' },
  { percent: 25, whole: 40, answer: 10, cat: 'easy' },
  { percent: 50, whole: 30, answer: 15, cat: 'easy' },
  { percent: 10, whole: 40, answer: 4,  cat: 'easy' },
];

// ── Medium partOf — non-benchmark percents ──
const MEDIUM_PART_OF: Spec[] = [
  { percent: 20, whole: 50, answer: 10, cat: 'medium' },
  { percent: 75, whole: 40, answer: 30, cat: 'medium' },
  { percent: 30, whole: 60, answer: 18, cat: 'medium' },
  { percent: 40, whole: 45, answer: 18, cat: 'medium' },
  { percent: 20, whole: 80, answer: 16, cat: 'medium' },
  { percent: 30, whole: 90, answer: 27, cat: 'medium' },
  { percent: 60, whole: 50, answer: 30, cat: 'medium' },
  { percent: 40, whole: 60, answer: 24, cat: 'medium' },
];

// ── Bridge — same as partOf, but show the fraction/decimal equivalence ──
const BRIDGE: Spec[] = [
  { percent: 50, whole: 90, answer: 45, cat: 'bridge', bridgeChip: { en: '50% = ½',  ar: '٥٠٪ = نصف' } },
  { percent: 25, whole: 60, answer: 15, cat: 'bridge', bridgeChip: { en: '25% = ¼',  ar: '٢٥٪ = رُبع' } },
  { percent: 20, whole: 50, answer: 10, cat: 'bridge', bridgeChip: { en: '20% = 0.2', ar: '٢٠٪ = ٠٫٢' } },
  { percent: 10, whole: 80, answer: 8,  cat: 'bridge', bridgeChip: { en: '10% = 0.1', ar: '١٠٪ = ٠٫١' } },
  { percent: 75, whole: 40, answer: 30, cat: 'bridge', bridgeChip: { en: '75% = ¾',  ar: '٧٥٪ = ثلاثة أرباع' } },
  { percent: 50, whole: 90, answer: 45, cat: 'bridge', bridgeChip: { en: '50% = 0.5', ar: '٥٠٪ = ٠٫٥' } },
  { percent: 10, whole: 70, answer: 7,  cat: 'bridge', bridgeChip: { en: '10% = 1/10', ar: '١٠٪ = ١؍١٠' } },
];

// ── Hard partOf — trickier single-digit percents ──
const HARD_PART_OF: Spec[] = [
  { percent: 15, whole: 80,  answer: 12, cat: 'hardPart' },
  { percent: 5,  whole: 120, answer: 6,  cat: 'hardPart' },
  { percent: 15, whole: 60,  answer: 9,  cat: 'hardPart' },
  { percent: 5,  whole: 80,  answer: 4,  cat: 'hardPart' },
  { percent: 15, whole: 40,  answer: 6,  cat: 'hardPart' },
];

// ── Hard findWhole — dial the VAULT SIZE (answer == whole) ──
const HARD_FIND_WHOLE: Spec[] = [
  { percent: 25, whole: 48, answer: 48, cat: 'hardWhole' }, // 12 = 25% of ?
  { percent: 20, whole: 50, answer: 50, cat: 'hardWhole' }, // 10 = 20% of ?
  { percent: 30, whole: 60, answer: 60, cat: 'hardWhole' }, // 18 = 30% of ?
  { percent: 10, whole: 90, answer: 90, cat: 'hardWhole' }, // 9  = 10% of ?
  { percent: 50, whole: 30, answer: 30, cat: 'hardWhole' }, // 15 = 50% of ?
  { percent: 25, whole: 24, answer: 24, cat: 'hardWhole' }, // 6  = 25% of ?
  { percent: 50, whole: 40, answer: 40, cat: 'hardWhole' }, // 20 = 50% of ?
];

// ── Hard findPercent — dial the PERCENT (answer == percent) ──
const HARD_FIND_PERCENT: Spec[] = [
  { percent: 30, whole: 60, answer: 30, cat: 'hardPercent' }, // 18 out of 60
  { percent: 25, whole: 48, answer: 25, cat: 'hardPercent' }, // 12 out of 48
  { percent: 50, whole: 60, answer: 50, cat: 'hardPercent' }, // 30 out of 60
  { percent: 10, whole: 60, answer: 10, cat: 'hardPercent' }, // 6  out of 60
  { percent: 25, whole: 60, answer: 25, cat: 'hardPercent' }, // 15 out of 60
  { percent: 10, whole: 90, answer: 10, cat: 'hardPercent' }, // 9  out of 90
  { percent: 40, whole: 50, answer: 40, cat: 'hardPercent' }, // 20 out of 50
];

function gcd(a: number, b: number): number {
  a = Math.abs(a); b = Math.abs(b);
  while (b) { [a, b] = [b, a % b]; }
  return a || 1;
}

/**
 * Choose a snap step that (a) evenly divides the dial and the answer, and
 * (b) yields a comfortable tick count (~14 ticks, never fewer than 6) so the
 * answer always lands ON a tick without crowding the ring.
 */
function computeTickStep(dialMax: number, answer: number): number {
  const g = gcd(dialMax, answer || dialMax);
  let best = 1;
  let bestScore = Infinity;
  for (let s = 1; s <= g; s++) {
    if (g % s !== 0) continue;
    const ticks = dialMax / s;
    if (ticks < 6) continue;
    const score = Math.abs(ticks - 14);
    if (score < bestScore) { bestScore = score; best = s; }
  }
  return best;
}

function subSkillFor(cat: Cat): VaultCrackerRound['subSkill'] {
  if (cat === 'bridge') return 'bridge';
  if (cat === 'hardWhole') return 'findWhole';
  if (cat === 'hardPercent') return 'findPercent';
  return 'partOf';
}

function difficultyFor(cat: Cat): DifficultyLevel {
  if (cat === 'easy') return DifficultyLevel.EASY;
  if (cat === 'medium') return DifficultyLevel.MEDIUM;
  return DifficultyLevel.HARD; // bridge + all hard categories
}

/** Guardrail for part-like rounds: answer must never equal the percent number. */
function validPartLike(s: Spec): boolean {
  if (!ALLOWED_PERCENTS.has(s.percent)) return false;
  const sub = subSkillFor(s.cat);
  if (sub === 'findPercent') return true; // answer == percent is expected here
  return s.answer !== s.percent;
}

function toRound(s: Spec, idx: number): VaultCrackerRound {
  const sub = subSkillFor(s.cat);
  // findWhole + findPercent dial 0→100; partOf/bridge dial 0→whole.
  const dialMax = sub === 'findWhole' || sub === 'findPercent' ? 100 : s.whole;
  const tickStep = computeTickStep(dialMax, s.answer);
  return {
    id: `vault-${idx}-${s.cat}-${s.percent}of${s.whole}=${s.answer}`,
    percent: s.percent,
    whole: s.whole,
    answer: s.answer,
    subSkill: sub,
    dialMax,
    tickStep,
    difficulty: difficultyFor(s.cat),
    ...(s.bridgeChip ? { bridgeChip: s.bridgeChip } : {})
  };
}

function keyOf(s: Spec): string { return `${s.cat}-${s.percent}-${s.whole}-${s.answer}`; }

function pick(pool: Spec[], used: Set<string>): Spec {
  const valid = pool.filter(validPartLike);
  const fresh = valid.filter(s => !used.has(keyOf(s)));
  const source = fresh.length ? fresh : valid;
  const chosen = source[Math.floor(Math.random() * source.length)];
  used.add(keyOf(chosen));
  return chosen;
}

/**
 * Build a session's rounds. For the default 7-round session this is the
 * ratified easy→hard interleave; for any other count it samples the same
 * pools proportionally along an easy→hard gradient.
 */
export function buildVaultCrackerRounds(count = 7): VaultCrackerRound[] {
  const used = new Set<string>();

  if (count === 7) {
    const specs: Spec[] = [
      pick(EASY_PART_OF, used),
      pick(EASY_PART_OF, used),
      pick(MEDIUM_PART_OF, used),
      pick(MEDIUM_PART_OF, used),
      pick(BRIDGE, used),
      pick(HARD_FIND_WHOLE, used),
      pick(HARD_FIND_PERCENT, used)
    ];
    return specs.map((s, i) => toRound(s, i));
  }

  // Generic fallback: a proportional easy→hard ramp across every pool.
  const gradient: Spec[][] = [
    EASY_PART_OF, EASY_PART_OF,
    MEDIUM_PART_OF, MEDIUM_PART_OF,
    BRIDGE,
    HARD_PART_OF, HARD_FIND_WHOLE, HARD_FIND_PERCENT
  ];
  const n = Math.max(1, count);
  const out: VaultCrackerRound[] = [];
  for (let i = 0; i < n; i++) {
    const band = gradient[Math.min(gradient.length - 1, Math.floor((i / n) * gradient.length))];
    out.push(toRound(pick(band, used), i));
  }
  return out;
}
